import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Button, Table, Form, FormGroup, Label, Input } from 'reactstrap';
import styled from 'styled-components';
import { studentActions, classActions, userActions, notificationActions } from '../../redux/actions';
import { studentService } from '../../services/student.service';
import { TooltipWrapper, TooltipContent, FormWrapper, StudentsContainer, StyledButton, StyledTableHeader, HeaderButtonsContainer, FilterInput, StyledTable, TableContainer, StudentCountContainer, StudentName, WarningButton, DangerButton, SuccessButton, SecondaryButton } from './StudentsManage.styled';
import ConfirmationPopup from '../Common/ConfirmationPopup';
import { FiUser, FiClock, FiStar } from 'react-icons/fi';
import StudentInformationModal from './StudentInformationModal';
import bankAccountService, { BankAccount } from '../../services/bankAccount.service';


// Helper: lấy ngày theo múi giờ LOCAL (tránh lỗi UTC khi dùng toISOString)
function localDateStr(d: Date = new Date()): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const StudentsManage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [archiveDate, setArchiveDate] = useState(localDateStr()); // YYYY-MM-DD
  const [studentToDelete, setStudentToDelete] = useState<any>(null);
  const [filterPendingOnly, setFilterPendingOnly] = useState(false);
  const [filterNewThisMonth, setFilterNewThisMonth] = useState(false);
  const [filterArchived, setFilterArchived] = useState(false);
  // — Archived students state —
  const [archivedStudents, setArchivedStudents] = useState<any[]>([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [studentToRestore, setStudentToRestore] = useState<any>(null);
  const [restoreClassId, setRestoreClassId] = useState('');
  const [restoreStartDate, setRestoreStartDate] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [hoverNewMonth, setHoverNewMonth] = useState(false); // để scale khi hover
  const [hoverUser, setHoverUser] = useState(false); // hover cho icon người
  const [showStudentInfoModal, setShowStudentInfoModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState<number | null>(null); // null = Tất cả
  // Inline gift editing state
  const [editingGiftId, setEditingGiftId] = useState<number | null>(null); // studentId đang sửa gift
  const [editingGiftValue, setEditingGiftValue] = useState<number>(0);
  const [savingGiftId, setSavingGiftId] = useState<number | null>(null);

  // Khi click vào icon người: reset mọi filter để hiện tất cả học sinh lại
  const resetFilters = () => {
    setFilterPendingOnly(false);
    setFilterNewThisMonth(false);
    setFilterArchived(false);
  };
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: null as string | null,
    phoneNumber: '',
    fbLink: '',
    note: '',
    address: '',
    bankAccountId: '' as string | number,
    classId: '',
    startDate: null as string | null,
    id: null as number | null,
    avatar: null as string | null, // base64 data URL
  });
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedSiblings, setSelectedSiblings] = useState<number[]>([]); // Siblings của học sinh đang edit
  const [siblingSearchTerm, setSiblingSearchTerm] = useState<string>(''); // Search term for sibling list
  const [originalFormData, setOriginalFormData] = useState<any>(null); // Store original values for edit mode
  const [originalSiblings, setOriginalSiblings] = useState<number[]>([]); // Store original siblings for edit mode
  const [isSaving, setIsSaving] = useState<boolean>(false); // Loading state for create/update operations
  const [studentsSiblingsMap, setStudentsSiblingsMap] = useState<Record<number, any[]>>({}); // Map of studentId -> siblings array
  // Image / crop states
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  // Check if form has changes (for edit mode) - use useMemo to recalculate when dependencies change
  const hasFormChanges = useMemo((): boolean => {
    if (!formData.id) return true; // Create mode: always allow if valid
    if (!originalFormData) return false;

    // Check if any field changed
    const fieldChanged =
      formData.name !== originalFormData.name ||
      formData.dateOfBirth !== originalFormData.dateOfBirth ||
      formData.phoneNumber !== originalFormData.phoneNumber ||
      formData.fbLink !== originalFormData.fbLink ||
      formData.note !== originalFormData.note ||
      formData.address !== originalFormData.address ||
      String(formData.bankAccountId) !== String(originalFormData.bankAccountId) ||
      formData.classId !== originalFormData.classId ||
      formData.startDate !== originalFormData.startDate ||
      formData.avatar !== originalFormData.avatar;

    // Check if siblings changed - compare length first for quick check
    if (selectedSiblings.length !== originalSiblings.length) {
      return true;
    }

    // Compare sorted arrays
    const selectedSorted = [...selectedSiblings].sort((a, b) => a - b);
    const originalSorted = [...originalSiblings].sort((a, b) => a - b);
    const siblingsChanged = selectedSorted.some((id, index) => id !== originalSorted[index]);

    return fieldChanged || siblingsChanged;
  }, [
    formData.name,
    formData.dateOfBirth,
    formData.phoneNumber,
    formData.fbLink,
    formData.note,
    formData.address,
    formData.bankAccountId,
    formData.classId,
    formData.startDate,
    formData.avatar,
    originalFormData,
    selectedSiblings,
    originalSiblings
  ]);

  const isDisabled: boolean = !formData.name || (formData.classId === '' || formData.classId == null) || (!formData.id && (formData.bankAccountId === '' || formData.bankAccountId == null)) || (formData.id ? !hasFormChanges : false) || isSaving;
  const [searchTerm, setSearchTerm] = useState('');  // State để lưu từ khoá tìm kiếm

  const classes = useAppSelector((state: any) => state.classes.classesList);
  const students = useAppSelector((state: any) => state.students.studentsList);
  const users = useAppSelector((state: any) => state.users.items || []);
  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);

  useEffect(() => {
    dispatch(classActions.getAll());
    dispatch(studentActions.getAll());
    // Load bank accounts
    bankAccountService.getAll().then(setBankAccounts).catch(() => setBankAccounts([]));
    // Load users (teachers and students) if not already loaded
    if (!users || users.length === 0) {
      dispatch(userActions.getAllTeachers?.());
      dispatch(userActions.getAllStudents?.());
    }
  }, [dispatch, users.length]);

  // Helper function to get user account info by studentId
  const getUserAccountInfo = (studentId: number | null) => {
    if (!studentId) return null;
    return users.find((u: any) => u.studentId === studentId);
  };

  // Load siblings for all students immediately after students are loaded
  useEffect(() => {
    const loadAllSiblings = async () => {
      if (!students || students.length === 0) return;

      try {
        // Use batch endpoint for all students at once
        const studentIds = students.map((s: any) => s.id);
        const siblingsMap = await studentService.getBatchSiblings(studentIds);
        setStudentsSiblingsMap(siblingsMap);
      } catch (error) {
        console.error('Error loading siblings:', error);
        setStudentsSiblingsMap({});
      }
    };

    loadAllSiblings();
  }, [students]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Khi chọn lớp: nếu đang tạo mới (formData.id == null) và tên lớp chứa "toán" -> thêm " - Toán" sau tên HS (nếu đã nhập tên)
  const handleClassSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedClassId = e.target.value;
    const cls = classes.find((c: any) => String(c.id) === String(selectedClassId));

    // Mặc định giữ nguyên tên hiện tại
    let currentName = formData.name || '';

    // Chỉ áp dụng auto-append khi đang tạo mới (formData.id == null)
    if (formData.id == null && cls) {
      // Loại bỏ suffix "- Toán" cũ nếu có (tránh bị lặp)
      currentName = currentName.replace(/\s*-\s*(toán|toan)$/i, '');

      const lowerClassName = String(cls.name).toLowerCase();
      const classHasToan = lowerClassName.includes('toán') || lowerClassName.includes('toan');

      if (classHasToan && currentName.trim() !== '') {
        currentName = `${currentName} - Toán`;
      }
    }

    setFormData({ ...formData, classId: selectedClassId, name: currentName });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const formattedDate = value ? new Date(value).toISOString() : null;
    setFormData({ ...formData, [name]: formattedDate });
  };

  // Read file -> dataURL
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result as string);
      setShowCropper(true);
      setCroppedImage(null);
    });
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Helper to create cropped image dataURL
  const getCroppedImg = useCallback(async (imageSrcLocal: string, pixelCrop: any) => {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = imageSrcLocal;
    });

    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas.toDataURL('image/jpeg');
  }, []);

  const applyCrop = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(cropped);
      setFormData((prev) => ({ ...prev, avatar: cropped }));
      setShowCropper(false);
    } catch (err) {
      console.error('Crop error', err);
    }
  }, [imageSrc, croppedAreaPixels, getCroppedImg]);

  const clearImage = () => {
    setImageSrc(null);
    setCroppedImage(null);
    setFormData((prev) => ({ ...prev, avatar: null }));
    setShowCropper(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleCreateStudent = async () => {
    setIsSaving(true);
    const updatedFormData = {
      ...formData,
      dateOfBirth: formData.dateOfBirth || null,
      startDate: formData.startDate || null,
      avatar: formData.avatar || null,
    };
    try {
      const result: any = await dispatch(studentActions.createStudent(updatedFormData));
      // action giờ return student trực tiếp: { id, name, ... }
      const newStudentId = result?.id;

      // Save siblings if any selected
      if (newStudentId && selectedSiblings.length > 0) {
        await studentService.updateSiblings(newStudentId, selectedSiblings);
      }

      dispatch(studentActions.getAll());
      resetForm();
    } catch (error) {
      console.error('Error creating student:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateStudent = async () => {
    setIsSaving(true);
    const updatedFormData = {
      ...formData,
      dateOfBirth: formData.dateOfBirth || null,
      startDate: formData.startDate || null,
      avatar: formData.avatar || null,
    };
    try {
      await dispatch(studentActions.updateStudent(updatedFormData));

      // Update siblings
      if (formData.id) {
        await studentService.updateSiblings(formData.id, selectedSiblings);
      }

      dispatch(studentActions.getAll());
      resetForm();
    } catch (error) {
      console.error('Error updating student:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveStudent = async (studentId: number, date: string) => {
    try {
      await studentService.archive(studentId, date);
      dispatch(studentActions.getAll());
      if (filterArchived) await loadArchivedStudents();
    } catch (err) {
      alert('Lỗi khi cho học sinh nghỉ: ' + err);
    }
    setShowDeletePopup(false);
    setStudentToDelete(null);
    setArchiveDate(localDateStr());
  };

  const loadArchivedStudents = async () => {
    setArchivedLoading(true);
    try {
      const data = await studentService.getArchived();
      setArchivedStudents(data || []);
    } catch (err) {
      setArchivedStudents([]);
    } finally {
      setArchivedLoading(false);
    }
  };

  // Xóa hẳn học sinh đã nghỉ
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<any>(null);
  const [isPermanentDeleting, setIsPermanentDeleting] = useState(false);

  const handlePermanentDelete = async () => {
    if (!permanentDeleteTarget) return;
    setIsPermanentDeleting(true);
    try {
      await studentService.permanentDelete(permanentDeleteTarget.id);
      setArchivedStudents(prev => prev.filter((s: any) => s.id !== permanentDeleteTarget.id));
      setPermanentDeleteTarget(null);
      dispatch(notificationActions.addNotification(`Đã xóa hẳn học sinh ${permanentDeleteTarget.name}.`, 'SUCCESS'));
    } catch (err: any) {
      dispatch(notificationActions.addNotification('Lỗi khi xóa: ' + err, 'DANGER'));
    } finally {
      setIsPermanentDeleting(false);
    }
  };

  const handleRestoreStudent = async () => {
    if (!studentToRestore || !restoreClassId) return;
    setRestoring(true);
    try {
      await studentService.restore(studentToRestore.id, {
        classId: Number(restoreClassId),
        startDate: restoreStartDate || undefined,
      });
      dispatch(studentActions.getAll());
      await loadArchivedStudents();
      setShowRestoreModal(false);
      setStudentToRestore(null);
      setRestoreClassId('');
      setRestoreStartDate('');
    } catch (err) {
      alert('Lỗi khi khôi phục học sinh: ' + err);
    } finally {
      setRestoring(false);
    }
  };

  const handleUpdateStudentListFromExcel = () => {
    dispatch(studentActions.addStudentListFromGoogleSheet());
    setShowUpdatePopup(false);
  };
  const handleOpenStudentInformation = (id: string) => {
    // Open modal instead of new page
    setSelectedStudentId(Number(id));
    setShowStudentInfoModal(true);
  };

  const handleEditStudent = async (student: any) => {
    const formDataToSet = {
      name: student.name,
      dateOfBirth: student.dateOfBirth,
      phoneNumber: student.phoneNumber,
      fbLink: student.fbLink,
      note: student.note,
      address: student.address || '',
      bankAccountId: student.bankAccountId || '',
      classId: student.classId,
      startDate: student.startDate,
      id: student.id,
      avatar: student.avatar ?? null, // add avatar to avoid TS error
    };
    setFormData(formDataToSet);
    setOriginalFormData(formDataToSet); // Store original for comparison

    // Load siblings
    try {
      const siblings = await studentService.getSiblings(student.id);
      const siblingIds = siblings.map((s: any) => s.id);
      setSelectedSiblings(siblingIds);
      setOriginalSiblings(siblingIds); // Store original siblings
    } catch (error) {
      console.error('Error loading siblings:', error);
      setSelectedSiblings([]);
      setOriginalSiblings([]);
    }

    setStudentModalOpen(true);
    window.scrollTo(0, 0);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      dateOfBirth: null,
      phoneNumber: '',
      fbLink: '',
      note: '',
      address: '',
      bankAccountId: '',
      classId: '',
      startDate: null,
      id: null,
      avatar: null,
    });
    setSelectedSiblings([]);
    setSiblingSearchTerm('');
    setOriginalFormData(null);
    setOriginalSiblings([]);
    setStudentModalOpen(false);
    // reset image states
    setImageSrc(null);
    setShowCropper(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCroppedImage(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // FastTooltip: hiển thị nhanh sau 250ms, dùng pointer events, cleanup đúng, và tái sử dụng được nhiều lần
  const FastTooltip: React.FC<{ text: string; children: React.ReactNode; delay?: number }> = ({ text, children, delay = 250 }) => {
    const [visible, setVisible] = useState(false);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }, []);

    const handleEnter = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        setVisible(true);
        timerRef.current = null;
      }, delay);
    };

    const handleLeave = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setVisible(false);
    };

    return (
      <span
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onPointerEnter={handleEnter}
        onPointerLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        aria-label={text}
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      >
        {children}
        {visible && (
          <div
            role="tooltip"
            style={{
              position: 'absolute',
              top: '-34px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#333',
              color: '#fff',
              padding: '6px 8px',
              borderRadius: 4,
              fontSize: 12,
              whiteSpace: 'nowrap',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            {text}
          </div>
        )}
      </span>
    );
  };

  // Helper: kiểm tra startDate có nằm trong tháng hiện tại hay không
  function isDateInCurrentMonth(dateString: string | null | undefined) {
    if (!dateString) return false;
    const d = new Date(dateString);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }

  // Đếm số học sinh đang học, chờ và mới trong tháng
  const totalActive = students?.filter((s: any) => s.startDate !== null).length || 0;
  const totalPending = students?.filter((s: any) => s.startDate === null).length || 0;
  const totalNewThisMonth = students?.filter((s: any) => isDateInCurrentMonth(s.startDate)).length || 0;

  const togglePendingFilter = () => {
    setFilterPendingOnly((prev) => !prev);
    setFilterNewThisMonth(false);
    setFilterArchived(false);
  };

  const toggleNewMonthFilter = () => {
    setFilterNewThisMonth(prev => !prev);
    setFilterPendingOnly(false);
    setFilterArchived(false);
  };

  const toggleArchivedFilter = () => {
    const next = !filterArchived;
    setFilterArchived(next);
    setFilterPendingOnly(false);
    setFilterNewThisMonth(false);
    if (next && archivedStudents.length === 0) loadArchivedStudents();
  };

  // Styled tag for class filters (similar to LessonDiary)
  const Tag = styled.span<{ isActive?: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: 6px 10px;
    border-radius: 16px;
    font-size: 13px;
    border: 1px solid ${props => (props.isActive ? '#007bff' : '#ddd')};
    color: ${props => (props.isActive ? '#fff' : '#333')};
    background: ${props => (props.isActive ? '#007bff' : '#f7f7f7')};
    cursor: pointer;
    user-select: none;
    transition: all 120ms ease-in-out;
    margin-right: 8px;
    margin-bottom: 8px;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }
  `;

  const filteredStudents = filterArchived
    ? (archivedStudents.filter((student: any) => {
      if (!searchTerm.trim()) return true;
      const s = searchTerm.toLowerCase();
      return (
        student.id.toString().includes(s) ||
        (student.name || '').toLowerCase().includes(s) ||
        (student.phoneNumber || '').toLowerCase().includes(s) ||
        (student.Class?.name || '').toLowerCase().includes(s)
      );
    }))
    : (students?.filter((student: any) => {
      if (selectedClassFilter !== null && student.classId !== selectedClassFilter) return false;
      if (filterPendingOnly && student.startDate !== null) return false;
      if (filterNewThisMonth && !isDateInCurrentMonth(student.startDate)) return false;
      const searchStr = searchTerm.toLowerCase();
      const studentName = student.name ? student.name.toLowerCase() : '';
      const studentPhone = student.phoneNumber ? student.phoneNumber.toLowerCase() : '';
      const studentFbLink = student.fbLink ? student.fbLink.toLowerCase() : '';
      const studentDob = student.dateOfBirth ? formatDate(student.dateOfBirth).toLowerCase() : '';
      const studentStartDate = student.startDate ? formatDate(student.startDate).toLowerCase() : '';
      const studentCreatedDate = student.createdDate ? formatDate(student.createdDate).toLowerCase() : '';
      const studentClass = classes?.find((cls: any) => cls.id === student.classId);
      const className = studentClass ? studentClass.name.toLowerCase() : '';
      return (
        student.id.toString().includes(searchStr) ||
        studentName.includes(searchStr) ||
        studentPhone.includes(searchStr) ||
        studentFbLink.includes(searchStr) ||
        studentDob.includes(searchStr) ||
        studentStartDate.includes(searchStr) ||
        studentCreatedDate.includes(searchStr) ||
        className.includes(searchStr)
      );
    }) || []);

  return (
    <StudentsContainer>
      <HeaderButtonsContainer>
        <div className="buttons-and-search">
          {userInfo?.role === 0 && (
            <>
              <StyledButton
                color="primary"
                onClick={() => setStudentModalOpen(true)}
                disabled={studentModalOpen}
              >
                Thêm Học Sinh
              </StyledButton>
              <StyledButton
                color="info"
                onClick={() => setShowUpdatePopup(true)}
              >
                Cập nhật Học Sinh (Excel)
              </StyledButton>
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FilterInput
              type="text"
              placeholder="Tìm kiếm học sinh..."
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
            />

            {/* 👇 Thêm tổng sĩ số - chỉ Admin xem được */}
            {userInfo?.role === 0 && (
              <StudentCountContainer>
                <span
                  className={`all ${(!filterPendingOnly && !filterNewThisMonth) ? 'active' : ''}`}
                  onClick={resetFilters}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 8 }}
                  onMouseEnter={() => setHoverUser(true)}
                  onMouseLeave={() => setHoverUser(false)}
                >
                  <FastTooltip text="Hiển thị tất cả học sinh">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, transform: hoverUser ? 'scale(1.08)' : 'none', transition: 'transform 120ms' }}>
                      <FiUser color="#28a745" />
                      <span>Studying: {totalActive}</span>
                    </span>
                  </FastTooltip>
                </span>
                <span
                  className={`pending ${filterPendingOnly ? 'active' : ''}`}
                  onClick={togglePendingFilter}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <FastTooltip text="HS Chưa bắt đầu học">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <FiClock color="#ff9800" />
                      <span>Waiting: </span>
                      <span>{totalPending}</span>
                    </span>
                  </FastTooltip>
                </span>
                {/* Icon mới: HS mới trong tháng */}
                <span
                  className={`new-month ${filterNewThisMonth ? 'active' : ''}`}
                  onClick={toggleNewMonthFilter}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}
                  onMouseEnter={() => setHoverNewMonth(true)}
                  onMouseLeave={() => setHoverNewMonth(false)}
                >
                  <FastTooltip text="HS mới trong tháng">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, transform: hoverNewMonth ? 'scale(1.08)' : 'none', transition: 'transform 120ms' }}>
                      <FiStar color="#007bff" />
                      <span>New this month: </span>
                      <span>{totalNewThisMonth}</span>
                    </span>
                  </FastTooltip>
                </span>
                {/* Filter: HS đã nghỉ */}
                <span
                  className={`archived ${filterArchived ? 'active' : ''}`}
                  onClick={toggleArchivedFilter}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}
                >
                  <FastTooltip text="Học sinh đã nghỉ tại trung tâm">
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: filterArchived ? '#c62828' : '#f5f5f5',
                      color: filterArchived ? '#fff' : '#555',
                      border: `1px solid ${filterArchived ? '#c62828' : '#ddd'}`,
                      borderRadius: 14, padding: '2px 10px', fontSize: 13,
                      fontWeight: filterArchived ? 700 : 400,
                      transition: 'all 0.15s',
                    }}>
                      🚩 Đã nghỉ{archivedLoading ? '...' : (archivedStudents.length > 0 ? `: ${archivedStudents.length}` : '')}
                    </span>
                  </FastTooltip>
                </span>
              </StudentCountContainer>
            )}
          </div>
        </div>
      </HeaderButtonsContainer>

      {/* Class filter tags (similar to LessonDiary) */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '8px 0 12px' }}>
        <Tag
          isActive={selectedClassFilter === null}
          onClick={() => setSelectedClassFilter(null)}
        >
          Tất cả
        </Tag>
        {[...(classes || [])]
          .slice()
          .sort((a: any, b: any) => a.name.localeCompare(b.name))
          .map((cls: any) => (
            <Tag
              key={cls.id}
              isActive={selectedClassFilter === cls.id}
              onClick={() => setSelectedClassFilter(cls.id)}
            >
              {cls.name}
            </Tag>
          ))}
      </div>


      {/* Modal thay cho formInline trước kia - chỉ cho Admin */}
      {userInfo?.role === 0 && (
        <Modal isOpen={studentModalOpen} toggle={() => setStudentModalOpen(false)} centered>
          <ModalHeader toggle={() => { setStudentModalOpen(false); resetForm(); }}>
            {formData.id ? 'Sửa Học Sinh' : 'Thêm Học Sinh'}
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label for="name">
                  Tên Học Sinh<span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="dateOfBirth">Ngày Sinh</Label>
                <Input
                  type="date"
                  name="dateOfBirth"
                  id="dateOfBirth"
                  value={formData.dateOfBirth ? formData.dateOfBirth.split('T')[0] : ''}
                  onChange={handleDateChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="phoneNumber">Số Điện Thoại</Label>
                <Input
                  type="text"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="fbLink">Link Facebook</Label>
                <Input
                  type="text"
                  name="fbLink"
                  id="fbLink"
                  value={formData.fbLink}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="note">Ghi chú (NOTE) :</Label>
                <Input
                  type="text"
                  name="note"
                  id="note"
                  value={formData.note}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="classId">
                  Lớp<span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="select"
                  name="classId"
                  id="classId"
                  value={formData.classId}
                  onChange={handleClassSelectChange}
                >
                  <option value="">Chọn Lớp</option>
                  {[...classes]
                    .sort((a: { name: string; }, b: { name: any; }) => a.name.localeCompare(b.name)) // Sắp xếp theo bảng chữ cái
                    .map((cls: any) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label for="startDate">Ngày Bắt Đầu Học</Label>
                <Input
                  type="date"
                  name="startDate"
                  id="startDate"
                  value={formData.startDate ? formData.startDate.split('T')[0] : ''}
                  onChange={handleDateChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="address">Địa chỉ nhà</Label>
                <Input
                  type="text"
                  name="address"
                  id="address"
                  placeholder="Số nhà, đường, phường/xã, quận/huyện..."
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup style={{ overflow: "hidden" }}>
                <Label for="bankAccountId">Tài khoản ngân hàng nhận học phí<span style={{ color: 'red' }}>*</span></Label>
                <Input
                  type="select"
                  name="bankAccountId"
                  id="bankAccountId"
                  value={formData.bankAccountId}
                  onChange={handleInputChange}
                >
                  <option value="">— Chọn tài khoản ngân hàng —</option>
                  {bankAccounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.bankName} — {acc.accountNumber} ({acc.accountHolder})
                    </option>
                  ))}
                </Input>
              </FormGroup>
              {/* Avatar upload & crop */}
              <FormGroup>
                <Label>Ảnh đại diện</Label>
                <Input type="file" accept="image/*" onChange={onFileChange} />
                {showCropper && imageSrc && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ position: 'relative', width: '100%', height: 300, background: '#333' }}>
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        Zoom
                        <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
                      </label>
                      <button type="button" className="btn btn-primary" onClick={applyCrop}>Áp dụng</button>
                      <button type="button" className="btn btn-secondary" onClick={clearImage}>Huỷ ảnh</button>
                    </div>
                  </div>
                )}
                {croppedImage && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ marginBottom: 6, fontSize: 12, color: '#666' }}>Preview</div>
                    <img src={croppedImage} alt="avatar preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />
                  </div>
                )}
              </FormGroup>

              {/* Siblings selection */}
              <FormGroup>
                <Label>Anh/Em (Siblings)</Label>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  Chọn anh/em ruột của học sinh này (để tính học phí cùng 1 lúc)
                </div>

                {/* Search input */}
                <Input
                  type="text"
                  placeholder="🔍 Tìm theo tên học sinh..."
                  value={siblingSearchTerm}
                  onChange={(e) => setSiblingSearchTerm(e.target.value)}
                  style={{
                    marginBottom: '8px',
                    fontSize: '13px',
                    padding: '6px 12px'
                  }}
                />

                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px'
                }}>
                  {students
                    .filter((s: any) => s.id !== formData.id) // Don't show current student
                    .filter((s: any) => {
                      if (!siblingSearchTerm.trim()) return true;
                      return s.name.toLowerCase().includes(siblingSearchTerm.toLowerCase());
                    })
                    .sort((a: any, b: any) => a.name.localeCompare(b.name))
                    .map((student: any) => {
                      const studentClass = classes.find((c: any) => c.id === student.classId);
                      return (
                        <div key={student.id} style={{ marginBottom: '4px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selectedSiblings.includes(student.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSiblings([...selectedSiblings, student.id]);
                                } else {
                                  setSelectedSiblings(selectedSiblings.filter(id => id !== student.id));
                                }
                              }}
                            />
                            <span>{student.name}</span>
                            <span style={{ fontSize: '11px', color: '#666' }}>
                              ({studentClass?.name || 'N/A'})
                            </span>
                          </label>
                        </div>
                      );
                    })}
                </div>
                {selectedSiblings.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#007bff', marginBottom: '6px' }}>
                      Đã chọn {selectedSiblings.length} anh/em:
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {selectedSiblings.map(siblingId => {
                        const sibling = students.find((s: any) => s.id === siblingId);
                        if (!sibling) return null;
                        const siblingClass = classes.find((c: any) => c.id === sibling.classId);
                        return (
                          <div
                            key={siblingId}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              backgroundColor: '#e7f3ff',
                              border: '1px solid #b3d9ff',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            <span>{sibling.name}</span>
                            <span style={{ color: '#666' }}>({siblingClass?.name || 'N/A'})</span>
                            <button
                              type="button"
                              onClick={() => setSelectedSiblings(selectedSiblings.filter(id => id !== siblingId))}
                              style={{
                                marginLeft: '4px',
                                padding: '0 4px',
                                border: 'none',
                                background: 'transparent',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                              title="Bỏ chọn"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </FormGroup>

            </Form>
          </ModalBody>
          <ModalFooter>
            <SuccessButton
              onClick={() => {
                if (formData.id) handleUpdateStudent();
                else handleCreateStudent();
              }}
              disabled={isDisabled}
            >
              {isSaving ? (
                <span>
                  <span style={{ marginRight: '8px' }}>⏳</span>
                  {formData.id ? 'Đang cập nhật...' : 'Đang thêm...'}
                </span>
              ) : (
                formData.id ? 'Cập Nhật Học Sinh' : 'Thêm Học Sinh'
              )}
            </SuccessButton>
            <SecondaryButton
              onClick={() => {
                resetForm();
              }}
              style={{ marginLeft: '10px' }}
            >
              Hủy
            </SecondaryButton>
          </ModalFooter>
        </Modal>
      )}

      {/* Filter header text (bold, centered, uppercase) shown above table when filters active */}
      {(filterPendingOnly || filterNewThisMonth) && (
        <div
          style={{
            textAlign: 'center',
            fontWeight: 700,
            textTransform: 'uppercase',
            margin: '12px 0',
            fontSize: 14,
          }}
        >
          {filterPendingOnly
            ? 'HỌC SINH CHƯA BẮT ĐẦU HỌC'
            : `HỌC SINH MỚI ĐÃ HỌC TRONG THÁNG ${new Date().getMonth() + 1}`}
        </div>
      )}
      {userInfo?.role === 0 && (
        <p>Total: {filteredStudents.length}</p>
      )}
      <TableContainer>
        <StyledTable striped>
          <thead>
            <tr>
              {userInfo?.role === 0 && <StyledTableHeader>ID</StyledTableHeader>}
              <StyledTableHeader>Ảnh</StyledTableHeader>
              <StyledTableHeader>Tên Học Sinh</StyledTableHeader>
              <StyledTableHeader>Lớp</StyledTableHeader>
              {userInfo?.role === 0 && <StyledTableHeader>Tài Khoản Làm BT</StyledTableHeader>}
              {userInfo?.role === 0 && <StyledTableHeader>Ghi chú</StyledTableHeader>}
              {userInfo?.role === 0 && <StyledTableHeader>Ngày Sinh</StyledTableHeader>}
              {userInfo?.role === 0 && <StyledTableHeader>Số Điện Thoại</StyledTableHeader>}
              <StyledTableHeader>Ngày Bắt Đầu</StyledTableHeader>
              <StyledTableHeader>Ngày Thêm</StyledTableHeader>
              {userInfo?.role === 0 && <StyledTableHeader>Cập Nhật</StyledTableHeader>}
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((student: any) => {
              const studentClass = classes.find((cls: any) => cls.id === student.classId);
              return (
                <tr key={student.id}>
                  {userInfo?.role === 0 && (
                    <td
                      style={{ cursor: 'pointer', color: 'red' }}
                      onClick={() => handleOpenStudentInformation(student.id)}
                    >
                      {student.id}
                    </td>
                  )}
                  <td
                    style={{ textAlign: 'center', padding: '8px' }}
                  >
                    <img
                      src={
                        student.avatar
                          ? `${process.env.REACT_APP_API_URL}${student.avatar}`
                          : "https://bootdey.com/img/Content/avatar/avatar7.png"
                      }
                      alt={student.name}
                      className="rounded-circle"
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleOpenStudentInformation(student.id)}
                    />
                    {/* Gift stars display / inline edit below avatar */}
                    {editingGiftId === student.id ? (
                      <div
                        style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                        onClick={e => e.stopPropagation()}
                      >
                        <input
                          type="number"
                          min={0}
                          autoFocus
                          value={editingGiftValue}
                          onChange={e => setEditingGiftValue(Number(e.target.value) || 0)}
                          onKeyDown={async e => {
                            if (e.key === 'Enter') {
                              setSavingGiftId(student.id);
                              try {
                                await dispatch(studentActions.updateStudent({
                                  id: student.id,
                                  name: student.name,
                                  dateOfBirth: student.dateOfBirth,
                                  phoneNumber: student.phoneNumber,
                                  fbLink: student.fbLink,
                                  note: student.note,
                                  classId: student.classId,
                                  startDate: student.startDate,
                                  gift: editingGiftValue,
                                }));
                                await dispatch(studentActions.getAll());
                              } finally {
                                setSavingGiftId(null);
                                setEditingGiftId(null);
                              }
                            } else if (e.key === 'Escape') {
                              setEditingGiftId(null);
                            }
                          }}
                          style={{
                            width: 52,
                            fontSize: 13,
                            textAlign: 'center',
                            padding: '2px 4px',
                            border: '2px solid #ffc107',
                            borderRadius: 6,
                            fontWeight: 700,
                            outline: 'none',
                          }}
                        />
                        <FiStar color="#ffc107" size={14} />
                        <button
                          title="Lưu (Enter)"
                          disabled={savingGiftId === student.id}
                          onClick={async e => {
                            e.stopPropagation();
                            setSavingGiftId(student.id);
                            try {
                              await dispatch(studentActions.updateStudent({
                                id: student.id,
                                name: student.name,
                                dateOfBirth: student.dateOfBirth,
                                phoneNumber: student.phoneNumber,
                                fbLink: student.fbLink,
                                note: student.note,
                                classId: student.classId,
                                startDate: student.startDate,
                                gift: editingGiftValue,
                              }));
                              await dispatch(studentActions.getAll());
                            } finally {
                              setSavingGiftId(null);
                              setEditingGiftId(null);
                            }
                          }}
                          style={{
                            background: '#28a745',
                            border: 'none',
                            borderRadius: 4,
                            color: 'white',
                            fontSize: 11,
                            padding: '2px 6px',
                            cursor: 'pointer',
                            fontWeight: 700,
                          }}
                        >
                          {savingGiftId === student.id ? '...' : '✓'}
                        </button>
                        <button
                          title="Hủy (Esc)"
                          onClick={e => { e.stopPropagation(); setEditingGiftId(null); }}
                          style={{
                            background: '#dc3545',
                            border: 'none',
                            borderRadius: 4,
                            color: 'white',
                            fontSize: 11,
                            padding: '2px 6px',
                            cursor: 'pointer',
                            fontWeight: 700,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          marginTop: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          cursor: userInfo?.role === 0 ? 'pointer' : 'default',
                          padding: '2px 8px',
                          borderRadius: 12,
                          background: student.gift > 0 ? 'linear-gradient(135deg,#fff8e1,#ffe082)' : '#f5f5f5',
                          border: student.gift > 0 ? '1px solid #ffc107' : '1px solid #e0e0e0',
                          transition: 'all 0.15s',
                          boxShadow: student.gift > 0 ? '0 1px 4px rgba(255,193,7,0.3)' : 'none',
                        }}
                        title={userInfo?.role === 0 ? 'Nhấn để sửa sao' : `${student.gift || 0} sao`}
                        onClick={e => {
                          e.stopPropagation();
                          if (userInfo?.role === 0) {
                            setEditingGiftId(student.id);
                            setEditingGiftValue(student.gift || 0);
                          }
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 700, color: student.gift > 0 ? '#856404' : '#999' }}>
                          {student.gift || 0}
                        </span>
                        <FiStar color={student.gift > 0 ? '#ffc107' : '#bbb'} size={13} />
                      </div>
                    )}
                  </td>
                  <td
                    style={{ cursor: 'pointer', color: 'red' }}
                    onClick={() => handleOpenStudentInformation(student.id)}
                  >
                    <StudentName>
                      {student.startDate ? (
                        <FiUser color="#28a745" />
                      ) : (
                        <span title="HS Chưa bắt đầu học" aria-label="HS Chưa bắt đầu học" style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                          <FiClock color="#ff9800" />
                        </span>
                      )}
                      {student.name}
                    </StudentName>
                    {studentsSiblingsMap[student.id] && studentsSiblingsMap[student.id].length > 0 ? (
                      <div style={{
                        fontSize: '11px',
                        color: '#666',
                        marginTop: '2px',
                        fontStyle: 'italic'
                      }}>
                        Anh/em: {studentsSiblingsMap[student.id].map((s: any) => {
                          const siblingClass = classes.find((c: any) => c.id === s.classId);
                          return `${s.name} (${siblingClass?.name || 'N/A'})`;
                        }).join('; ')}
                      </div>
                    ) : null}
                  </td>
                  <td>{studentClass ? studentClass.name : 'Không có lớp'}</td>
                  {userInfo?.role === 0 && (
                    <td>
                      {(() => {
                        const userAccount = getUserAccountInfo(student.id);
                        if (!userAccount) return 'Chưa có';
                        return (
                          <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                            <div><strong>Tài khoản:</strong> {userAccount.userName}</div>
                            <div><strong>Mật khẩu:</strong> 123456</div>
                          </div>
                        );
                      })()}
                    </td>
                  )}
                  {userInfo?.role === 0 && (
                    <td>
                      {student.note ?
                        <TooltipWrapper>
                          Xem ghi chú
                          {/* Đây là phần tử chứa nội dung có thể chỉnh sửa */}
                          <TooltipContent
                            className="tooltip-content"
                            contentEditable={true}
                            suppressContentEditableWarning={true}
                          >
                            {student.note}
                          </TooltipContent>
                        </TooltipWrapper> : ''
                      }
                    </td>
                  )}
                  {userInfo?.role === 0 && <td>{student.dateOfBirth ? formatDate(student.dateOfBirth) : ''}</td>}
                  {userInfo?.role === 0 && (
                    <td>
                      {student.phoneNumber ?
                        <TooltipWrapper>
                          Xem SĐT
                          {/* Đây là phần tử chứa nội dung có thể chỉnh sửa */}
                          <TooltipContent
                            className="tooltip-content"
                            contentEditable={true}
                            suppressContentEditableWarning={true}
                          >
                            {student.phoneNumber}
                          </TooltipContent>
                        </TooltipWrapper> : ''
                      }
                    </td>
                  )}
                  <td>
                    {student.startDate ? formatDate(student.startDate) : ''}
                    {filterArchived && student.archivedAt && (
                      <div style={{ fontSize: '0.75rem', color: '#c62828', marginTop: 2, fontStyle: 'italic' }}>
                        🚫 Cho nghỉ: {formatDate(student.archivedAt)}
                      </div>
                    )}
                  </td>
                  <td>{student.createdAt ? formatDate(student.createdAt) : ''}</td>
                  {userInfo?.role === 0 && (
                    <td>
                      {filterArchived ? (
                        <>
                          <WarningButton onClick={() => handleEditStudent(student)} style={{ marginRight: 4 }}>Sửa</WarningButton>
                          <SuccessButton
                            onClick={() => {
                              setStudentToRestore(student);
                              setRestoreClassId('');
                              setRestoreStartDate(localDateStr());
                              setShowRestoreModal(true);
                            }}
                          >
                            ✅ Nhập học lại
                          </SuccessButton>
                          {' '}
                          <DangerButton
                            style={{ background: 'linear-gradient(135deg,#b71c1c,#c62828)', marginLeft: 4 }}
                            onClick={() => setPermanentDeleteTarget(student)}
                          >
                            🗑️ Xóa hẳn
                          </DangerButton>
                        </>
                      ) : (
                        <>
                          <WarningButton onClick={() => handleEditStudent(student)}>Sửa</WarningButton>{' '}
                          <DangerButton onClick={() => {
                            setStudentToDelete(student);
                            setShowDeletePopup(true);
                          }}>
                            Cho nghỉ
                          </DangerButton>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
      </TableContainer>


      {/* Modal xác nhận cho học sinh nghỉ - có chọn ngày nghỉ */}
      {userInfo?.role === 0 && showDeletePopup && studentToDelete && (
        <Modal isOpen={showDeletePopup} toggle={() => setShowDeletePopup(false)} centered>
          <ModalHeader toggle={() => setShowDeletePopup(false)}>
            🛝 Cho học sinh nghỉ: <strong>{studentToDelete?.name}</strong>
          </ModalHeader>
          <ModalBody>
            <p style={{ marginBottom: 12 }}>
              Học sinh <strong>{studentToDelete?.name}</strong> sẽ được đánh dấu là <strong>đã nghỉ</strong>.
            </p>
            <ul style={{ fontSize: '0.9rem', color: '#555', paddingLeft: 18, marginBottom: 14 }}>
              <li>Học sinh sẽ <strong>biến mất khỏi danh sách chính</strong>.</li>
              <li>Tài khoản đăng nhập sẽ bị xóa.</li>
              <li>Lịch sử <strong>học phí</strong> vẫn được giữ nguyên.</li>
              <li>Nếu chọn ngày nghỉ trong <strong>quá khứ</strong>, bản ghi điểm danh từ ngày đó đến hôm nay sẽ bị xóa.</li>
            </ul>
            <FormGroup>
              <Label><strong>Ngày nghỉ thực tế:</strong></Label>
              <Input
                type="date"
                value={archiveDate}
                max={localDateStr()}
                onChange={(e) => setArchiveDate(e.target.value)}
              />
              {archiveDate < localDateStr() && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '10px 14px', marginTop: 8 }}>
                  <p style={{ color: '#856404', fontSize: '0.85rem', marginBottom: 6, fontWeight: 600 }}>
                    ⚠️ Lưu ý khi chọn ngày nghỉ trong quá khứ:
                  </p>
                  <ul style={{ color: '#856404', fontSize: '0.82rem', marginBottom: 0, paddingLeft: 18 }}>
                    <li>Điểm danh của <strong>{studentToDelete?.name}</strong> từ ngày <strong>{archiveDate}</strong> đến hôm nay sẽ bị <strong>xóa</strong>.</li>
                    <li style={{ marginTop: 4 }}>
                      Học phí tháng <strong>{archiveDate.slice(0, 7).replace('-', '/')}</strong> sẽ thay đổi theo số buổi điểm danh còn lại.
                      Sau khi xác nhận, hãy vào <strong>Tính học phí</strong> → tính lại HP tháng đó cho học sinh này.
                    </li>
                  </ul>
                </div>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowDeletePopup(false)}>Hủy</Button>
            <Button
              color="danger"
              onClick={() => handleArchiveStudent(studentToDelete?.id, archiveDate)}
            >
              Xác nhận cho nghỉ
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {userInfo?.role === 0 && showUpdatePopup && (
        <ConfirmationPopup
          isOpen={showUpdatePopup}
          onClose={() => setShowUpdatePopup(false)} title="Xác nhận cập nhật học sinh từ file Excel" message={
            <>
              Khi cập nhật danh sách học sinh từ file Excel:<br />
              - Lấy tất cả id học sinh trong <a href="https://docs.google.com/spreadsheets/d/1vf--cgdKLupf1nLHySAazN2RvTZtfF9EwphoJ7BaB0A/edit?gid=1525671082#gid=1525671082" target="_blank">file Excel</a> và trong database <br />
              - id nào chưa có trong database thì thêm mới học sinh đó vào DB, có rồi thì chỉ update thông tin từ sheet sang DB <br />
              - id học sinh nào mà có trong DB mà lại không có trong sheet, thì xóa học sinh đó trong DB, và xóa thông tin điểm danh trong bảng attendanceStudentRecords<br />
            </>
          }
          onConfirm={() => handleUpdateStudentListFromExcel()}
        />
      )}

      {/* Restore Modal */}
      {showRestoreModal && studentToRestore && (
        <Modal isOpen={showRestoreModal} toggle={() => setShowRestoreModal(false)} centered>
          <ModalHeader toggle={() => setShowRestoreModal(false)}>
            ↩ Cho học sinh học lại: <strong>{studentToRestore.name}</strong>
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Chọn lớp mới <span style={{ color: 'red' }}>*</span></Label>
              <Input
                type="select"
                value={restoreClassId}
                onChange={(e) => setRestoreClassId(e.target.value)}
              >
                <option value="">— Chọn lớp —</option>
                {[...(classes || [])].sort((a: any, b: any) => a.name.localeCompare(b.name)).map((cls: any) => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Ngày bắt đầu học lại</Label>
              <Input
                type="date"
                value={restoreStartDate}
                onChange={(e) => setRestoreStartDate(e.target.value)}
              />
            </FormGroup>
            <p style={{ fontSize: '0.85rem', color: '#666' }}>
              Thông tin học sinh (tên, sđt, ngày sinh...) sẽ được giữ nguyên từ hồ sơ cũ.<br />
              Tài khoản đăng nhập sẽ được tạo lại tự động.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="success" disabled={!restoreClassId || restoring} onClick={handleRestoreStudent}>
              {restoring ? 'Đang xử lý...' : '↩ Xác nhận cho học lại'}
            </Button>
            <Button color="secondary" onClick={() => setShowRestoreModal(false)}>Huỷ</Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Student Information Modal */}
      {selectedStudentId && (
        <StudentInformationModal
          show={showStudentInfoModal}
          onClose={() => {
            setShowStudentInfoModal(false);
            setSelectedStudentId(null);
          }}
          studentId={selectedStudentId}
        />
      )}

      {/* Modal xác nhận xóa hẳn học sinh */}
      {permanentDeleteTarget && (
        <Modal isOpen toggle={() => setPermanentDeleteTarget(null)}>
          <ModalHeader toggle={() => setPermanentDeleteTarget(null)}>⚠️ Xóa hẳn học sinh</ModalHeader>
          <ModalBody>
            <p>Bạn đang chuẩn bị <strong>xóa hẳn vĩnh viễn</strong> học sinh:</p>
            <p style={{ fontSize: '1.1rem', fontWeight: 700, color: '#c62828' }}>{permanentDeleteTarget.name}</p>
            <p>Những gì sẽ bị xóa:</p>
            <ul style={{ fontSize: '0.9rem' }}>
              <li>❌ Lịch sử điểm danh</li>
              <li>❌ Bài thi đã nộp &amp; câu trả lời</li>
              <li>❌ Câu sai đã theo dõi</li>
              <li>❌ Lịch sử bảng xếp hạng flashcard</li>
              <li>❌ Lịch sử ôn tập</li>
              <li>❌ Tài khoản đăng nhập</li>
              <li>❌ Quan hệ anh/em ruột</li>
            </ul>
            <p style={{ color: '#1b5e20', fontWeight: 600 }}>✅ Giữ lại: Học phí đã thu (snapshot tên) — không ảnh hưởng thống kê doanh thu.</p>
            <p style={{ color: '#c62828', fontWeight: 700, borderTop: '1px solid #eee', paddingTop: 8 }}>Hành động này <u>không thể hoàn tác</u>!</p>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" disabled={isPermanentDeleting} onClick={handlePermanentDelete}>
              {isPermanentDeleting ? 'Đang xóa...' : '🗑️ Xác nhận xóa hẳn'}
            </Button>
            <Button color="secondary" onClick={() => setPermanentDeleteTarget(null)}>Huỷ</Button>
          </ModalFooter>
        </Modal>
      )}
    </StudentsContainer>
  );
};

export default StudentsManage;


